import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl-next';

import { xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { CommentBody } from '@confluence/inline-comments-common/entry-points/CommentBody';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import {
	CommentType,
	useCommentsDataState,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';

const linkStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: token('color.background.neutral.subtle'),
	color: token('color.text.accent.gray'),
	font: token('font.body.small'),
	fontWeight: token('font.weight.bold'),
	paddingBlock: token('space.0'),
	paddingInline: token('space.050'),
	borderRadius: token('radius.small'),
	outline: `1px solid ${token('color.background.accent.blue.subtle')}`,
	textDecoration: 'none',
	marginLeft: token('space.050'),
	'&:hover': {
		cursor: 'pointer',
	},
});

const popupContainerStyles = xcss({
	width: '360px',
	backgroundColor: 'elevation.surface.overlay',
	borderRadius: 'radius.small',
	boxShadow: 'elevation.shadow.overlay',
	maxHeight: '480px',
	overflow: 'auto',
	padding: 'space.200', // Uniform padding all around
});

// Replies container - matches comments panel structure
const repliesContainerStyles = xcss({
	paddingLeft: 'space.200', // Base offset for replies container (16px)
	paddingRight: 'space.100',
	paddingBottom: 'space.100',
	marginTop: 'space.negative.100', // Pull up to connect with parent line (close the gap)
});

// Individual reply wrapper - provides proper indentation
const replyBoxStyles = xcss({
	paddingLeft: 'space.300', // Additional indentation for each reply (24px)
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

type CommentReferenceLinkProps = {
	commentId: string;
	children: React.ReactNode;
	commentData?: any; // Optional: allows passing mock data for testing/demo purposes
	openPopupId?: string | null;
	onOpenChange?: (isOpen: boolean, commentId: string) => void;
};

// Helper to find comment by ID in the comments store
const findCommentById = (commentsDataMap: any, commentId: string) => {
	// Search in inline comments
	if (commentsDataMap?.inline) {
		for (const threadKey in commentsDataMap.inline) {
			const comment = commentsDataMap.inline[threadKey];
			if (comment?.id === commentId) {
				return comment;
			}
			// Check replies
			if (comment?.replies) {
				const reply = comment.replies.find((r: any) => r.id === commentId);
				if (reply) return reply;
			}
		}
	}

	// Search in general/footer comments
	if (commentsDataMap?.general) {
		for (const threadKey in commentsDataMap.general) {
			const comment = commentsDataMap.general[threadKey];
			if (comment?.id === commentId) {
				return comment;
			}
			// Check replies
			if (comment?.replies) {
				const reply = comment.replies.find((r: any) => r.id === commentId);
				if (reply) return reply;
			}
		}
	}

	return null;
};

const CommentSnippetContent = ({ commentData }: { commentData: any }) => {
	const supportedActions: CommentAction[] = [];

	if (!commentData || !commentData.body || !commentData.author) {
		return (
			<Box xcss={popupContainerStyles} testId="comment-snippet">
				<div>
					<FormattedMessage
						id="comments-panel.comment-reference-link.not-available"
						defaultMessage="Comment not available"
						description="Error message shown when a comment cannot be loaded in the popup"
					/>
				</div>
			</Box>
		);
	}

	let contentString: string;
	if (commentData.body?.value) {
		contentString = commentData.body.value;
	} else if (typeof commentData.body === 'string') {
		contentString = commentData.body;
	} else if (typeof commentData.body === 'object') {
		contentString = JSON.stringify(commentData.body);
	} else {
		contentString = '';
	}

	const hasReplies = commentData.replies && commentData.replies.length > 0;

	return (
		<Box xcss={popupContainerStyles} testId="comment-snippet">
			{/* Parent comment - CommentBody has internal margin/padding */}
			<Box>
				<CommentBody
					pageId={commentData.pageId || 'unknown'}
					pageType="page"
					commentId={commentData.id}
					content={contentString}
					displayName={commentData.author.displayName}
					avatarUrl={commentData.author.profilePicture?.path}
					userId={commentData.author.accountId}
					date={commentData.version?.when ?? commentData.createdAtNonLocalized ?? ''}
					createdAt={commentData.createdAt?.value ?? ''}
					createdAtNonLocalized={commentData.createdAtNonLocalized ?? ''}
					versionDate={commentData.version?.when ?? ''}
					permissions={{
						isEditable: false,
						isRemovable: false,
						isResolvable: false,
					}}
					isReply={false}
					isEdited={false}
					mode="view"
					numReplies={commentData.replies?.length || 0}
					isFabricPage
					isCommentsPanel
					supportedActions={supportedActions}
					commentType={CommentType.GENERAL}
					isCommentActive
					canAddComments={hasReplies}
					hideBranchingStyle={false}
				/>
			</Box>

			{/* Replies */}
			{hasReplies && (
				<Box xcss={repliesContainerStyles}>
					{commentData.replies.map((reply: any, idx: number) => {
						let replyContentString = '';
						if (reply.body && typeof reply.body === 'object' && reply.body.value) {
							replyContentString = reply.body.value;
						} else if (typeof reply.body === 'string') {
							replyContentString = reply.body;
						} else if (typeof reply.body === 'object') {
							replyContentString = JSON.stringify(reply.body);
						}

						const isLastReply = idx === commentData.replies.length - 1;

						return (
							<Box key={reply.id} xcss={replyBoxStyles}>
								<CommentBody
									pageId={commentData.pageId || 'unknown'}
									pageType="page"
									commentId={reply.id}
									content={replyContentString}
									displayName={reply.author.displayName}
									avatarUrl={reply.author.profilePicture?.path}
									userId={reply.author.accountId}
									date={reply.version?.when ?? reply.createdAtNonLocalized ?? ''}
									createdAt={reply.createdAt?.value ?? ''}
									createdAtNonLocalized={reply.createdAtNonLocalized ?? ''}
									versionDate={reply.version?.when ?? ''}
									permissions={{
										isEditable: false,
										isRemovable: false,
										isResolvable: false,
									}}
									isReply
									isEdited={false}
									parentCommentId={commentData.id}
									mode="view"
									numReplies={0}
									isFabricPage
									isCommentsPanel
									supportedActions={supportedActions}
									commentType={CommentType.GENERAL}
									isCommentActive
									canAddComments={false}
									hideBranchingStyle={isLastReply}
									showRepliesWithCurvedBorders
								/>
							</Box>
						);
					})}
				</Box>
			)}
		</Box>
	);
};

export const CommentReferenceLinkWrapper: React.FC<CommentReferenceLinkProps> = ({
	commentId,
	children,
	commentData: mockCommentData,
	openPopupId,
	onOpenChange,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isClickOpen, setIsClickOpen] = useState(false);
	const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Get real comment data from the store
	const { commentsDataMap } = useCommentsDataState();
	const { Popup } = useChoreographerAssets();

	const realCommentData = useMemo(() => {
		return findCommentById(commentsDataMap, commentId);
	}, [commentsDataMap, commentId]);

	// Use real data if available, otherwise fall back to mock (for testing/demo)
	const commentData = realCommentData || mockCommentData;

	// Close this popup if another one is opened
	useEffect(() => {
		if (openPopupId && openPopupId !== commentId && isOpen) {
			setIsOpen(false);
			setIsClickOpen(false);
		}
	}, [openPopupId, commentId, isOpen]);

	const handleMouseEnter = useCallback(() => {
		// Only show on hover if not opened by click
		if (!isClickOpen) {
			hoverTimeoutRef.current = setTimeout(() => {
				setIsOpen(true);
				onOpenChange?.(true, commentId);
			}, 300);
		}
	}, [isClickOpen, commentId, onOpenChange]);

	const handleMouseLeave = useCallback(() => {
		// Clear hover timeout
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
		}
		// Close popup if opened by hover (not by click)
		if (!isClickOpen) {
			setIsOpen(false);
			onOpenChange?.(false, commentId);
		}
	}, [isClickOpen, commentId, onOpenChange]);

	const handlePopupClose = useCallback(() => {
		setIsOpen(false);
		setIsClickOpen(false);
		onOpenChange?.(false, commentId);
	}, [commentId, onOpenChange]);

	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();

			// Toggle click state
			const newClickState = !isClickOpen;
			setIsClickOpen(newClickState);
			setIsOpen(newClickState);
			onOpenChange?.(newClickState, commentId);

			// Clear any hover timeout
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
				hoverTimeoutRef.current = null;
			}
		},
		[isClickOpen, commentId, onOpenChange],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				e.stopPropagation();

				// Toggle click state
				const newClickState = !isClickOpen;
				setIsClickOpen(newClickState);
				setIsOpen(newClickState);
				onOpenChange?.(newClickState, commentId);

				// Clear any hover timeout
				if (hoverTimeoutRef.current) {
					clearTimeout(hoverTimeoutRef.current);
					hoverTimeoutRef.current = null;
				}
			}
		},
		[isClickOpen, commentId, onOpenChange],
	);

	// Accessibility: onFocus behaves like onMouseEnter
	const handleFocus = handleMouseEnter;

	// Accessibility: onBlur behaves like onMouseLeave
	const handleBlur = handleMouseLeave;

	if (!commentData) {
		return <>{children}</>;
	}

	return (
		<Popup
			isOpen={isOpen}
			onClose={handlePopupClose}
			placement="top-start"
			autoFocus={false}
			shouldRenderToParent={false}
			content={() => <CommentSnippetContent commentData={commentData} />}
			trigger={(triggerProps) => (
				<span
					css={linkStyles}
					{...triggerProps}
					role="button"
					tabIndex={0}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onFocus={handleFocus}
					onBlur={handleBlur}
					onClick={handleClick}
					onKeyDown={handleKeyDown}
					data-testid={`comment-reference-link-${commentId}`}
				>
					{children}
				</span>
			)}
		/>
	);
};
